import React from 'react';
import { CheckCircle2, AlertTriangle, Clock, CircleSlash } from 'lucide-react';

/**
 * ValidationStatus — Trust Infrastructure component.
 *
 * Displays the validation status of a module against its ICAR/SAU pilot
 * target. Surfaces last-validated date for pattern: "validated within
 * the last 18 months" else demote to BETA.
 *
 * Props:
 *   status: 'validated' | 'beta' | 'pilot-pending' | 'no-data' (required)
 *   lastValidated: ISO date string or null
 *   pilot: optional partner name e.g. 'ICAR-CAZRI Jodhpur'
 */

const STATUS_CONFIG = {
  validated: {
    label: 'Validated',
    icon: CheckCircle2,
    bg: '#E6F4EA',
    fg: '#1B5E20',
    border: '#A5D6A7',
  },
  beta: {
    label: 'BETA · pilot in progress',
    icon: AlertTriangle,
    bg: '#FFF8E1',
    fg: '#7C5800',
    border: '#FFE082',
  },
  'pilot-pending': {
    label: 'Pilot pending',
    icon: Clock,
    bg: '#ECEFF1',
    fg: '#37474F',
    border: '#CFD8DC',
  },
  'no-data': {
    label: 'No validation data yet',
    icon: CircleSlash,
    bg: '#F5F2EC',
    fg: '#8A8580',
    border: '#E5E7DD',
  },
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export default function ValidationStatus({ status = 'no-data', lastValidated = null, pilot = null }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['no-data'];
  const Icon = cfg.icon;
  const dateStr = fmtDate(lastValidated);
  return (
    <span
      className="inline-flex items-center gap-2"
      data-testid={`validation-status-${status}`}
    >
      <span
        className="inline-flex items-center gap-1.5"
        style={{
          background: cfg.bg,
          color: cfg.fg,
          border: `1px solid ${cfg.border}`,
          borderRadius: 999,
          padding: '4px 10px',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.01em',
        }}
      >
        <Icon size={13} strokeWidth={2.2} />
        {cfg.label}
      </span>
      {(dateStr || pilot) && (
        <span className="font-body" style={{ fontSize: 11.5, color: '#6F6A63', fontStyle: 'italic' }}>
          {pilot ? `${pilot}` : ''}{dateStr ? `${pilot ? ' · ' : ''}${dateStr}` : ''}
        </span>
      )}
    </span>
  );
}
